import { type BillingPlanDto, REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { FormattedText, Loader, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { SubscriptionPlanCard } from 'src/modules/payment/components'
import { PlanChangeModals } from 'src/modules/payment/components/plan-change-modals'
import { PlansContent } from 'src/modules/payment/components/plans-content'
import { useFreePlanCheckout } from 'src/modules/payment/hooks/use-free-plan-checkout'
import { usePlanChangeFlow } from 'src/modules/payment/hooks/use-plan-change-flow'
import { usePlansFunnelTracking } from 'src/modules/payment/hooks/use-plans-funnel-tracking'
import { useUpdatePaymentMethod } from 'src/modules/payment/hooks/use-update-payment-method'
import { SubscriptionStatus } from 'src/modules/payment/types/payment.types'
import { isPlanNotForSale } from 'src/modules/payment/utils/helpers'
import type { PlansOfferProps } from './plans-offer.interface'
import classes from './plans-offer.module.scss'
import { usePlansOffer } from './use-plans-offer'

export function PlansOffer({ surface, params, priceSuffix, plansType, showCancelLink }: PlansOfferProps) {
  const offer = usePlansOffer(plansType)
  const planChangeFlow = usePlanChangeFlow(offer.isYearly)
  const freePlanCheckout = useFreePlanCheckout()
  const { openUpdateCard, isAvailable: isUpdateCardAvailable, isAwaitingUpdate } = useUpdatePaymentMethod()
  const trackUpgradeClicked = usePlansFunnelTracking({
    surface,
    params,
    account: offer.account,
    plans: offer.filteredPlans,
    recommendedPlanId: offer.recommendedPlanId,
  })
  const { account, isYearly } = offer
  const { openCheckout } = freePlanCheckout
  const { startPlanChange } = planChangeFlow

  const handleSelectPlan = useCallback(
    (plan: BillingPlanDto) => {
      trackUpgradeClicked(plan)

      if (account?.isFreeTrial) {
        openCheckout(plan, { trigger: params.trigger, isYearly })
        return
      }

      startPlanChange(plan)
    },
    [account?.isFreeTrial, isYearly, openCheckout, params.trigger, startPlanChange, trackUpgradeClicked],
  )

  if (offer.isLoading || !account || planChangeFlow.isLoading || freePlanCheckout.isLoading) {
    return <Loader isLoading />
  }

  return (
    <div className={classes.root}>
      {isPlanNotForSale(offer.currentPlan) && <NotForSalePlan offer={offer} />}
      <PlansContent
        account={account}
        sentCount={offer.profile?.reminders_sent_during_billing_cycle ?? 0}
        freeRemindersLimit={offer.profile?.effective_free_reminders_limit}
        notSentCount={offer.notSentCount}
        currentPlan={offer.currentPlan}
        subscriptionEndedAt={offer.subscriptionEndedAt}
        billingPausedAt={offer.billingPausedAt}
        onUpdateCard={openUpdateCard}
        isUpdateCardAvailable={isUpdateCardAvailable}
        isAwaitingUpdate={isAwaitingUpdate}
        plans={offer.availablePlans}
        isYearly={isYearly}
        onIntervalChange={offer.setIsYearly}
        currentPlanId={offer.subscription?.plan_id}
        recommendedPlanId={offer.recommendedPlanId}
        priceSuffix={priceSuffix}
        onSelectPlan={handleSelectPlan}
      />
      {showCancelLink &&
        !account.isFreeTrial &&
        offer.subscription?.state !== SubscriptionStatus.deleted &&
        account.pausedReason !== REMINDERS_PAUSED_REASONS.paymentPaused && <CancelPlanLink />}
      <PlanChangeModals flow={planChangeFlow} currentPlanName={offer.currentPlan.name} />
    </div>
  )
}

function NotForSalePlan({ offer }: { offer: ReturnType<typeof usePlansOffer> }) {
  const { formatMessage } = useIntl()

  return (
    <div>
      <Typography variant="body" className={classes.notForSaleTitle}>
        <FormattedText id="payment.notForSale.title" />
      </Typography>
      <Typography variant="span" className={classes.notForSaleText}>
        <FormattedText id="payment.notForSale.text" values={{ br: () => <br /> }} />
      </Typography>
      <SubscriptionPlanCard
        plan={{
          ...offer.currentPlan,
          description: formatMessage({ id: 'payment.notForSale.description' }),
          benefits: null,
        }}
        currentPlanId={offer.subscription?.plan_id}
        handleUpgradePlan={() => () => undefined}
        className={classes.notForSaleTitle}
      />
      <Typography variant="h1" className={classes.changeTitle}>
        <FormattedText id="payment.title" />
      </Typography>
    </div>
  )
}

function CancelPlanLink() {
  const navigate = useNavigate()

  return (
    <Typography variant="span" className={classes.cancelButton} onClick={() => navigate(routeNames.cancelPlan.path)}>
      <FormattedText id="payment.button.cancelPlan" />
    </Typography>
  )
}
